'use strict'

import Vue from 'vue'
import axios from 'axios'

import VacancyApi from '@/api/vacancy.js'
import DictionaryApi from '@/api/dictionary.js'
import PermissionsApi from '@/api/permissions.js'

import countries from './dictionary/countries.js'

import { parseResponse, handleError } from '@/_helpers/utils.js'

const STEP_STATUS_PRIORITY = 10

const state = {
    error: null,
    responseStatuses: [],
    responseStages: [],
    vacancies: [],
    permissions: [],
    userRoles: [],
    admin_roles: [],
    loading: false,
    loadingStatuses: false,
    updatingStatusesLocalIds: [],
    temporaryStorageDeletedStatuses: []
}

const actions = {
    fetch({ commit, rootState, dispatch }, without_vacancies = null) {
        commit('fetch')

        const statusesParams = {
            with_count: 0
        }
        if (rootState.responses.filters.vacancy && rootState.responses.filters.vacancy.id !== 'all') {
            statusesParams.vacancy_id = rootState.responses.filters.vacancy.id
        }

        const requests = [
            DictionaryApi.fetchStatuses(statusesParams),
            DictionaryApi.fetchStages(),
        ]

        let filter = [];
        filter['active'] = true;

        if (!without_vacancies) [
            requests.push(VacancyApi.fetchList({ filter }))
        ]

        return Promise.all(requests).then(data => {
            commit('fetchSuccess', {
                // Filter here (not in getters),
                // because otherwise we have get some troubles
                // when try to add new status (work by index in array)
                responseStatuses: data[0].filter(s => !s.is_hidden),
                responseStages: data[1],
                vacancies: !without_vacancies ? data[2].data : []
            })
        }).catch(err => {
            dispatch('global/setError', err, { root: true })
            commit('fetchFailure', err)
        })
    },

    fetchStagesAndStatuses({ commit, rootState, dispatch }, model) {
        commit('fetch')

        let statusesParams = {
            with_count: 'top'
        };

        if (model) {
            statusesParams = model;
        }
        if (rootState.responses.filters.vacancy && rootState.responses.filters.vacancy.id !== 'all') {
            statusesParams.vacancy_id = rootState.responses.filters.vacancy.id
        }

        return Promise.all([
            DictionaryApi.fetchStatuses(statusesParams),
            DictionaryApi.fetchStages(),
        ]).then(data => {
            commit('fetchSuccess', {
                // Filter here (not in getters),
                // because otherwise we have get some troubles
                // when try to add new status (work by index in array)
                responseStatuses: data[0].filter(s => !s.is_hidden),
                responseStages: data[1]
            })
            return data;
        }).catch(err => {
            dispatch('global/setError', err, { root: true })
            commit('fetchFailure', err)
        })
    },

    fetchPermissions({ commit, dispatch }) {
        commit('fetch')
        return PermissionsApi.fetchPermissions()
            .then(permissions => {
                commit('fetchPermissionsSuccess', permissions)
                return permissions
            })
            .catch(err => {
                dispatch('global/setError', err, { root: true })
                commit('fetchFailure', err)
            })
    },

    fetchAdminUserRoles({ commit, dispatch }) {
        return axios.get('/1.0/admin/company-user-roles')
            .then(response => {
                const data = parseResponse(response).data;

                commit('updateUserRoles', data);
            })
            .catch(err => {
                const error = handleError(err);
                dispatch('global/setError', error, { root: true })
            })
    },

    fetchUserRoles({ commit, dispatch }) {
        return axios.get('/1.0/company-user-roles')
            .then(response => {
                const data = parseResponse(response).data;

                commit('updateUserRoles', data);
            })
            .catch(err => {
                const error = handleError(err);
                dispatch('global/setError', error, { root: true })
            })
    },

    fetch_admin_roles({ commit, dispatch }) {
        return axios.get('/1.0/admin/users-admin/roles')
            .then(response => {
                const data = parseResponse(response).data;

                commit('update_admin_roles', data);
            })
            .catch(err => {
                const error = handleError(err);
                dispatch('global/setError', error, { root: true })
            })
    },

    updateResponseStatus({ commit, dispatch }, status) {
        commit('updateResponseStatus', status.id)

        return DictionaryApi.updateStatuses([status])
            .then(res => commit('updateResponseStatusSuccess', res.pop()))
            .catch(err => {
                commit('updateResponseStatusFailure', {
                    err,
                    id: status.id
                })
                return Promise.reject(err)
            })
    },

    updateResponseStatusesPriority({ commit, dispatch }, { statuses, fromIndex }) {
        const lastElement = [...statuses].pop()
        let priority = lastElement ? lastElement.priority : fromIndex * STEP_STATUS_PRIORITY

        const updatingStatuses = statuses
            .filter(s => !s.is_hidden)
            .filter((s, index) => index >= fromIndex)
            .map(v => {
                const s = Object.assign({}, v, {
                    priority: priority + STEP_STATUS_PRIORITY
                })
                priority += STEP_STATUS_PRIORITY
                return s
            })

        commit('updateResponseStatuses', {
            newOrderedStatuses: statuses,
            updatingStatusesIds: updatingStatuses.map(i => i.id)
        })

        DictionaryApi.updateStatuses(updatingStatuses)
            .then(res => commit('updateResponseStatusesSuccess', res))
            .catch(err => {
                dispatch('global/setError', err, { root: true })
                commit('updateResponseStatusesFailure', err)
            })
    },

    deleteResponseStatuses({ commit, dispatch }, { id }) {
        commit('deleteResponseStatuses', id)

        DictionaryApi.deleteStatuses([{ id }])
            .then(res => commit('deleteStatusSuccess', res.pop()))
            .catch(err => {
                dispatch('global/setError', err, { root: true })
                commit('deleteStatusFailure', err)
            })
    },

    finallyDelete({ commit }, { id }) {
        commit('finallyDelete', id)
    },

    undoFinallyDelete({ commit, dispatch }, { id }) {
        const restoredStatus = state.temporaryStorageDeletedStatuses
            .find(i => i.id === id)
        const restoredStatusOldId = restoredStatus.id

        commit('undoFinallyDelete', id)

        DictionaryApi.createStatuses([restoredStatus])
            .then(res => {
                const newStatusFromRestored = res.pop()
                commit('undoFinallyDeleteSuccess', {
                    oldId: restoredStatusOldId,
                    newStatusFromRestored: newStatusFromRestored
                })

                return newStatusFromRestored.id
            })
            .then(id => {
                commit('initFakeLoading', id)
                return id
            })
            .then(id => {
                commit('clearFakeLoading', id)
            })
            .catch(err => {
                dispatch('global/setError', err, { root: true })
                commit('undoFinallyDeleteFailure', err)
            })
    },

    createResponseStatus({ commit, dispatch }, name) {
        const lastElement = [...state.responseStatuses].pop()
        const priority = lastElement ? (lastElement.priority + 10) : 0

        const newStatus = {
            localId: lastElement.localId + 1 || 1,
            name: name,
            priority: priority,
            on_top: false
        }
        commit('createResponseStatus', newStatus)

        return DictionaryApi.createStatuses([newStatus])
            .then(res => {
                const createdNewStatus = res.pop()

                commit('createResponseStatusSuccess', {
                    oldId: newStatus.id,
                    createdNewStatus: createdNewStatus
                })

                return createdNewStatus.id
            })
            .then(id => {
                commit('initFakeLoading', id)
                return id
            })
            .then(id => {
                commit('clearFakeLoading', id)
            })
            .catch(err => {
                commit('createResponseStatusFailure', {
                    err,
                    oldId: newStatus.id
                })
                return Promise.reject(err)
            })
    }
}

const mutations = {
    fetch(state) {
        state.loading = true
        state.loadingStatuses = true
    },

    save(state) {
        state.loading = true
    },

    fetchSuccess(state, {
        responseStatuses = state.responseStatuses,
        responseStages = state.responseStages,
        vacancies = state.vacancies
    }) {
        state.responseStatuses = responseStatuses.map(item => {
            return Object.assign(item, {
                localId: item.id
            })
        })
        state.responseStages = responseStages
        state.vacancies = vacancies
        state.loading = false
        state.loadingStatuses = false

        const updatedIds = responseStatuses.map(item => item.id)
        state.updatingStatusesLocalIds = state.updatingStatusesLocalIds
            .filter(i => !updatedIds.includes(i))
    },

    fetchPermissionsSuccess(state, permissions) {
        state.loading = false
        state.permissions = permissions
    },

    fetchFailure(state, err) {
        state.error = err
        console.error(err)
    },

    updateResponseStatus(state, localId) {
        state.loading = true
        state.updatingStatusesLocalIds.push(localId)
    },

    updateResponseStatusSuccess(state, status) {
        state.loading = false
        state.responseStatuses.map(s => {
            if (s.id === status.id) {
                Object.assign(s, status)
            }

            return s
        })
        state.updatingStatusesLocalIds = state.updatingStatusesLocalIds
            .filter(i => i !== status.id)
    },

    updateResponseStatusFailure(state, { err, id }) {
        state.loading = false
        state.err = err
        let pos = state.updatingStatusesLocalIds.indexOf(id);
        state.updatingStatusesLocalIds.splice(pos, 1);
    },

    updateResponseStatuses(state, { newOrderedStatuses, updatingStatusesIds }) {
        state.loading = true
        state.responseStatuses = newOrderedStatuses
        state.updatingStatusesLocalIds.push(...updatingStatusesIds)
    },

    updateResponseStatusesSuccess(state, statuses) {
        state.loading = true
        const updatedStatusesIds = statuses.map(s => s.id)
        statuses = statuses.map(s => {
            return Object.assign(s, {
                localId: s.id
            })
        })

        state.responseStatuses = state.responseStatuses
            .map(s => {
                if (updatedStatusesIds.includes(s.id)) {
                    const newStatus = statuses.find(st => st.id === s.id)
                    s = Object.assign({}, s, newStatus)
                }

                return s
            })
            .sort((a, b) => a.priority - b.priority)
        state.updatingStatusesLocalIds = state.updatingStatusesLocalIds
            .filter(i => !updatedStatusesIds.includes(i))
    },

    updateResponseStatusesFailure(state, err) {
        state.loading = false
        state.error = err
    },

    deleteResponseStatuses(state, id) {
        state.loading = true
        state.updatingStatusesLocalIds.push(id)
    },

    deleteStatusSuccess(state, status) {
        state.loading = false
        state.updatingStatusesLocalIds = state.updatingStatusesLocalIds
            .filter(i => i !== status.id)
        const deletedStatus = state.responseStatuses
            .find(s => s.id === status.id)
        Vue.set(deletedStatus, 'deleted', true)
        // Save for 10s (exact time in Setting-Status-Row.vue file)
        // this status for possibillity to undo deletion
        state.temporaryStorageDeletedStatuses.push(status)
    },

    deleteStatusFailure(state, err) {
        state.loading = false
        console.error(err)
    },

    finallyDelete(state, id) {
        state.temporaryStorageDeletedStatuses = state.temporaryStorageDeletedStatuses
            .filter(s => s.id !== id)
        state.responseStatuses = state.responseStatuses
            .filter(s => s.id !== id)
    },

    undoFinallyDelete(state, id) {
        state.loading = true
        state.responseStatuses = state.responseStatuses.map(s => {
            if (s.id === id) {
                Object.assign(s, {
                    deleted: false
                })
            }

            return s
        })
        state.updatingStatusesLocalIds.push(id)
    },

    undoFinallyDeleteSuccess(state, { oldId, newStatusFromRestored }) {
        state.loading = false
        state.updatingStatusesLocalIds = state.updatingStatusesLocalIds
            .filter(id => id !== oldId)
        state.responseStatuses = state.responseStatuses.map(s => {
            if (s.id === oldId) {
                s = Object.assign({}, s, newStatusFromRestored, {
                    localId: newStatusFromRestored.id
                })
            }

            return s
        })
    },

    undoFinallyDeleteFailure(state, err) {
        state.loading = false
        state.err = err
    },

    // Hack for show spinner loading and SAVED label,
    // because id was changed, we cannot detect this replacement
    // from row and simulate the loading for new id of status
    initFakeLoading(state, id) {
        state.updatingStatusesLocalIds.push(id)
    },

    clearFakeLoading(state, loadingId) {
        state.updatingStatusesLocalIds = state.updatingStatusesLocalIds
            .filter(id => id !== loadingId)
    },

    createResponseStatus(state, newStatus) {
        state.loading = true
        state.responseStatuses.push(newStatus)
        state.updatingStatusesLocalIds.push(newStatus.localId)
    },

    createResponseStatusSuccess(state, { oldId, createdNewStatus }) {
        state.loading = false
        state.updatingStatusesLocalIds = state.updatingStatusesLocalIds
            .filter(id => id !== oldId)
        state.responseStatuses = state.responseStatuses.map(s => {
            if (s.id === oldId) {
                Object.assign(s, createdNewStatus, {
                    localId: createdNewStatus.id
                })
            }

            return s
        })
    },

    createResponseStatusFailure(state, { err, oldId }) {
        state.err = err
        let ids = state.responseStatuses.map(s => {
            return s.id
        })
        let pos = ids.indexOf(oldId);
        state.responseStatuses.splice(pos, 1)
    },

    updateUserRoles(state, roles) {
        state.userRoles = roles
    },

    update_admin_roles(state, roles) {
        state.admin_roles = roles
    }
}

const getters = {
    error(state) {
        return state.error
    },

    loading(state) {
        return state.loading
    },

    loadingStatuses(state) {
        return state.loadingStatuses
    },

    responseStatuses(state) {
        return state.responseStatuses
    },

    responseStages(state) {
        return state.responseStages
    },

    vacancies(state) {
        return state.vacancies
    },

    permissions(state) {
        return state.permissions
    },

    userRoles(state) {
        return state.userRoles
    },

    admin_roles(state) {
        return state.admin_roles
    },

    updatingStatusesLocalIds(state) {
        return state.updatingStatusesLocalIds
    }
}

export default {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
    modules: {
        countries
    }
}
